import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Table, Form, Alert } from 'react-bootstrap';
import { FaSearchDollar } from 'react-icons/fa';
import PredictionChart from './PredictionChart';
import SiteCard from './SiteCard';
import './HomeLookupDemo.css';

// Formatting helpers mirror PropertyCatalog so the demo reads like the real tool.
const formatGBP = (n) =>
    n == null ? '-' : `£${Math.round(n).toLocaleString('en-GB')}`;

const formatGBPBin = (n, bin = 25000) => {
    if (n == null) return '-';
    const lower = Math.floor(n / bin) * bin;
    const fmt = (v) => v >= 1_000_000
        ? `£${(v / 1_000_000).toLocaleString('en-GB', { maximumFractionDigits: 3 })}m`
        : `£${(v / 1000).toLocaleString('en-GB')}k`;
    return `${fmt(lower)} - ${fmt(lower + bin)}`;
};

const isSupportedUrl = (url) =>
    /^https?:\/\/(www\.)?rightmove\.co\.uk\//i.test(url.trim());

// Deterministic build-time sample: a seeded LCG keeps the histogram stable
// across renders and avoids firing the live lookup for logged-out visitors.
const SAMPLE_ESTIMATES = (() => {
    let seed = 987654321;
    const next = () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
    };
    return Array.from({ length: 100 }, () => {
        const u = (next() + next() + next() + next()) / 4;
        return Math.round((435000 + (u - 0.5) * 240000) / 500) * 500;
    });
})();

const SAMPLE_SORTED = [...SAMPLE_ESTIMATES].sort((a, b) => a - b);

const SAMPLE = {
    listing: {
        postcode_area: 'OX5',
        property_type: 'Semi-detached',
        total_floor_area: 96,
        tenure: 'Freehold',
        new_build: false,
    },
    valuation: {
        median: SAMPLE_SORTED[50],
        low: SAMPLE_SORTED[10],
        high: SAMPLE_SORTED[90],
        estimates: SAMPLE_ESTIMATES,
    },
    comparables: [
        ['2024-11-12', '14 Rutten Lane, Yarnton', 'Semi-detached', 425000, 155],
        ['2024-07-03', '9 Cassington Road, Yarnton', 'Semi-detached', 452000, 240],
        ['2024-05-21', '3 Bell Close', 'Terraced', 388000, 310],
    ].map(([date, address, type, price, distance_metres]) =>
        ({ date, address, type, price, distance_metres })),
};

function HomeLookupDemo() {
    const navigate = useNavigate();
    const [url, setUrl] = useState('');
    const [hint, setHint] = useState('');

    const goToSignup = () => navigate('/members');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (url && !isSupportedUrl(url)) {
            setHint('Please paste a Rightmove listing URL.');
            return;
        }
        goToSignup();
    };

    const v = SAMPLE.valuation;
    const listing = SAMPLE.listing;
    const chartData = v.estimates.map((value) => ({ value }));

    return (
        <Container className="d-flex flex-column align-items-center">
            <Row className="justify-content-center my-5 mx-0" style={{ width: '100%' }}>
                <Col md={12}>
                    <SiteCard
                        header={<><FaSearchDollar className="mr-2" /> INSTANT VALUATION</>}
                        content={
                            <Container fluid>
                                <p className="home-lookup-intro">
                                    Paste a Rightmove listing for a machine-learning valuation and
                                    nearby sold prices.
                                </p>

                                <Form onSubmit={handleSubmit} className="home-lookup-form" role="search">
                                    <Form.Control
                                        type="url"
                                        aria-label="Rightmove listing URL"
                                        placeholder="https://www.rightmove.co.uk/properties/12345678"
                                        value={url}
                                        onChange={(e) => { setUrl(e.target.value); setHint(''); }}
                                    />
                                    <button type="submit" className="otta-button">Value it</button>
                                </Form>
                                {hint && <Alert variant="warning" className="mt-2">{hint}</Alert>}

                                <div className="home-lookup-example">
                                    <span className="home-lookup-label">Example</span>
                                    <Row className="g-4">
                                        <Col md={5} xs={12}>
                                            <h3 className="home-lookup-h3">Inputs</h3>
                                            <Table size="sm" borderless className="home-lookup-kv">
                                                <tbody>
                                                    <tr><th>Postcode</th><td>{listing.postcode_area}</td></tr>
                                                    <tr><th>Type</th><td>{listing.property_type}</td></tr>
                                                    <tr><th>Floor area</th><td>{Math.round(listing.total_floor_area)} m²</td></tr>
                                                    <tr><th>Tenure</th><td>{listing.tenure}</td></tr>
                                                </tbody>
                                            </Table>
                                        </Col>

                                        <Col md={7} xs={12}>
                                            <h3 className="home-lookup-h3">Valuation</h3>
                                            <div className="home-lookup-big-number">{formatGBPBin(v.median)}</div>
                                            <div className="home-lookup-range">
                                                10th-90th percentile: {formatGBP(v.low)} - {formatGBP(v.high)}
                                            </div>
                                            <div className="home-lookup-chart-wrap">
                                                <PredictionChart data={chartData} />
                                            </div>
                                        </Col>
                                    </Row>

                                    <h3 className="home-lookup-h3 mt-3">Nearby sales</h3>
                                    <div className="table-responsive">
                                        <Table striped bordered hover size="sm" className="home-lookup-comparables-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Address</th>
                                                    <th>Type</th>
                                                    <th className="text-end">Price</th>
                                                    <th className="text-end">Distance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {SAMPLE.comparables.map((c, i) => (
                                                    <tr key={i}>
                                                        <td>{c.date}</td>
                                                        <td>{c.address}</td>
                                                        <td>{c.type}</td>
                                                        <td className="text-end">{formatGBP(c.price)}</td>
                                                        <td className="text-end">{Math.round(c.distance_metres)}m</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="home-lookup-cta">
                                        Sign up free to value your own property.
                                        <button type="button" className="otta-button" onClick={goToSignup}>
                                            Create free account
                                        </button>
                                    </div>
                                </div>
                            </Container>
                        }
                    />
                </Col>
            </Row>
        </Container>
    );
}

export default HomeLookupDemo;
